import * as yup from 'yup';
import { OptionType } from '../CommonComponents/OPL/Select/BaseSelect/types';

export enum AllocationStrategyType {
  MarketAssumptions,
  PortfolioGroup,
  HypotheticalReturn,
  ExistingAccount,
  AdviserSettings,
}

export enum EarmarkType {
  DebtManagement = 7,
  AllGoals = 9,
}

export enum LoanPayoffStrategy {
  HighestRateFirst = 0,
  LowestBalanceFirst = 1,
}

export interface AssetClassChart {
  name: string;
  y: number;
  color_group: number;
  color?: string;
}

export interface Item {
  Id: number;
  GroupId: number;
  PortfolioName: string;
  PortfolioDescription: string;
  HighLevelChart: AssetClassChart[];
  DetailedAssetAllocation: AssetClassChart[];
}

export interface Group {
  GroupId: number;
  GroupName: string;
  ModelPortfolios: Item[];
}

export enum ExternalSystemId {
  Orion = 2,
  Riskalyze = 5,
}

export interface AssetClassDTO {
  color: string | null;
  color_group: number;
  id: number;
  name: string | null;
  y: number;
}

export interface ModelDTO {
  assetAllocationInputModel: AssetClassDTO[];
  riskScore: number | null;
  hypotheticalReturn: number | null;
  id: string;
  orionModelId: number | null;
  portfolioDescription: string | null;
  portfolioName: string;
  riskRange: {
    from: number;
    to: number;
  };
}

export interface ModelGroupDTO {
  adviserId: number | null;
  createdBy: {
    name: string;
    email: string;
  } | null;
  externalSystem: ExternalSystemId | null;
  groupId: string;
  groupName: string;
  modelPortfolios: ModelDTO[];
}

export interface EarmarkValueDTO {
  earmarkType: number;
  dependent: {
    dependentType: number;
    id: string;
    isDependent: boolean;
    personalInfo: {
      contactInformation: string | null;
      dob: string;
      firstName: string;
      lastName: string;
      yearOfBirth: number | null;
    };
  } | null;
  name: string | null;
}

export const hasDebtManagementGoalOption = (
  earmarkOptions?: OptionType<EarmarkValueDTO>[] | null,
) =>
  earmarkOptions?.some(
    option => option.value?.earmarkType === EarmarkType.DebtManagement,
  ) === true;

export const includesDebtManagementEarmark = (
  earmark?: EarmarkValueDTO | null,
  hasDebtManagementGoal = false,
) =>
  earmark?.earmarkType === EarmarkType.DebtManagement
  || (earmark?.earmarkType === EarmarkType.AllGoals && hasDebtManagementGoal);

export const isDebtManagementOnlyEarmark = (earmarks?: EarmarkValueDTO[] | null) =>
  Array.isArray(earmarks)
  && earmarks.length > 0
  && earmarks.every(earmark => earmark?.earmarkType === EarmarkType.DebtManagement);

export interface ManageExcessCashDTO {
  modelPortfoliosGroups: ModelGroupDTO[];
  options: {
    accountList: OptionType<string>[];
    defaultAssetAllocation: AssetClassDTO[];
    earmarkOptions: OptionType<EarmarkValueDTO>[];
    loanPayoffStrategyOptions?: OptionType<LoanPayoffStrategy>[];
    loanPayoffStrategy?: LoanPayoffStrategy | null;
    hasOrionModels: boolean;
    excessCashAllocation: {
      excessCashAllocationDetails: AllocationStrategyData | null;
      excessCashAllocationType: AllocationStrategyType;
      excessCashGoalEarmark: EarmarkValueDTO[] | null;
      loanPayoffStrategy?: LoanPayoffStrategy | null;
    };
    excessCashAllocationTypes: { label: string; value: AllocationStrategyType }[];
  };
}

export interface AllocationStrategyData {
  marketAssumptions: AssetClassDTO[] | null;
  modelPortfolios: ModelDTO | null;
  hypotheticalReturnPercent: number | null;
  existingAccountId: string | null;
}

export interface FormValues {
  ExcessCashGoalEarmark: EarmarkValueDTO[] | null;
  LoanPayoffStrategy: LoanPayoffStrategy | null;
  ExcessCashFlowStrategy: {
    type?: AllocationStrategyType | null;
    data: AllocationStrategyData | null;
  };
}

export const FORM_ID = 'client-settings--manage-excess-cash-modal-form';

export const MODAL_HEADER_ID = 'client-settings--manage-excess-cash-modal-header';

const savedCashSourceSchema = yup.object({
  type: yup.mixed<AllocationStrategyType>().required('Source is required'),

  data: yup.mixed().when('type', type => {
    switch (type) {
      case AllocationStrategyType.MarketAssumptions:
        return yup.object({
          marketAssumptions: yup
            .array<OptionType<string>[]>()
            .test('check-total', 'The total must add upto 100%', arr => {
              if (!arr || !Array.isArray(arr)) {
                return false;
              }
              const sum = arr.reduce((acc, obj) => acc + (parseFloat(obj.y) || 0), 0);
              return sum >= 100;
            }),
        });
      case AllocationStrategyType.PortfolioGroup:
        return yup.object({
          modelPortfolios: yup.object({
            id: yup
              .string()
              .nullable()
              .required('Portfolio Name is required'),
          }),
        });
      case AllocationStrategyType.HypotheticalReturn:
        return yup.object({
          hypotheticalReturnPercent: yup
            .number()
            .required('Hypothetical Return Percentage is required')
            .typeError('The value must be between 0% and 100%')
            .nullable()
            .transform((value, originalValue) =>
              String(originalValue).trim() === '' ? null : value,
            )
            .min(0, 'The value must be between 0% and 100%')
            .max(100, 'The value must be between 0% and 100%'),
        });
      case AllocationStrategyType.ExistingAccount:
        return yup.object({
          existingAccountId: yup
            .string()
            .required('Account Name is required')
            .nullable(),
        });
      default:
        return yup.mixed().nullable();
    }
  }),
});

export const createValidation = (hasDebtManagementGoal: boolean) =>
  yup.object().shape({
    LoanPayoffStrategy: yup
      .number()
      .nullable()
      .test('loan-payoff-required', 'Loan payoff strategy is required', function isLoanPayoffRequired(
        value,
      ) {
        const earmarks = this.parent.ExcessCashGoalEarmark as EarmarkValueDTO[] | null;
        const strategyType = this.parent.ExcessCashFlowStrategy?.type as
          | AllocationStrategyType
          | undefined;
        const hasDebtManagement =
          Array.isArray(earmarks)
          && earmarks.some(earmark =>
            includesDebtManagementEarmark(earmark, hasDebtManagementGoal),
          );
        const skipBecauseExistingAccount =
          strategyType === AllocationStrategyType.ExistingAccount
          && !isDebtManagementOnlyEarmark(earmarks);

        if (!hasDebtManagement || skipBecauseExistingAccount) {
          return true;
        }

        return value != null;
      }),
    ExcessCashFlowStrategy: yup.mixed().when('ExcessCashGoalEarmark', {
      is: isDebtManagementOnlyEarmark,
      then: yup.object({
        type: yup.mixed<AllocationStrategyType>().nullable(),
        data: yup.mixed().nullable(),
      }),
      otherwise: savedCashSourceSchema,
    }),
  });
